<template>
  <div class="game-count-trend-mobile">
    <!-- 图表容器 -->
    <div class="chart-card">
      <div class="card-header">
        <span class="card-title">比赛级别趋势</span>
        <div class="card-actions">
          <!-- 比赛级别选择器 -->
          <el-select
            v-model="selectedLevels"
            multiple
            collapse-tags
            placeholder="选择比赛级别"
            class="header-select"
            @change="handleLevelChange"
          >
            <div class="select-actions">
              <el-button size="mini" type="text" @click.stop.prevent="handleSelectAll">全选</el-button>
              <el-divider direction="vertical" />
              <el-button size="mini" type="text" @click.stop.prevent="handleReset">重置</el-button>
            </div>
            <el-option
              v-for="item in allLevels"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExport" />
          </el-tooltip>
          <el-tooltip content="全屏显示" placement="top">
            <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreen" />
          </el-tooltip>
        </div>
      </div>
      <div class="chart-container" ref="chart"></div>
    </div>

    <!-- 全屏弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="95%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
    >
      <div slot="title" class="dialog-header">
        <span>比赛级别趋势</span>
        <div class="dialog-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          <el-button type="text" icon="el-icon-close" @click="closeDialog" />
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import _ from 'lodash'

// 添加辅助函数
function round(value, decimals) {
  return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals)
}

function sum(arr) {
  return arr.reduce((a, b) => a + b, 0)
}

export default {
  name: 'GameCountTrendMobile',
  data() {
    return {
      chart: null,
      chartData: {
        months: [],
        levels: [],
        series: [],
        scores: {},
        ranks: {},
        totals: {}
      },
      fullscreenChart: null,
      dialogVisible: false,
      resizeHandler: null,
      isInitializing: false,
      selectedLevels: [],
      allLevels: [],
      loading: false
    }
  },
  mounted() {
    this.initChart()
    this.fetchLevels()
    this.fetchData()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
    if (this.fullscreenChart) {
      this.fullscreenChart.dispose()
    }
    window.removeEventListener('resize', this.resizeChart)
  },
  watch: {
    dialogVisible(val) {
      if (!val && this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    }
  },
  methods: {
    // 工具：dataURL 转 Blob
    dataURLToBlob(dataURL) {
      const arr = dataURL.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) u8arr[n] = bstr.charCodeAt(n)
      return new Blob([u8arr], { type: mime })
    },
    // 工具：下载 Blob
    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = filename
      link.href = url
      document.body.appendChild(link)
      link.click()
      URL.revokeObjectURL(url)
      document.body.removeChild(link)
    },
    initChart() {
      this.chart = echarts.init(this.$refs.chart)
    },
    resizeChart() {
      this.chart?.resize()
    },
    async fetchData() {
      try {
        this.loading = true
        const res = await request.get('/api/analysis/game/count_trend')
        this.chartData = {
          months: res.data.months || [],
          levels: res.data.levels || [],
          series: res.data.series || [],
          scores: res.data.scores || {},
          ranks: res.data.ranks || {},
          totals: res.data.totals || {}
        }
        this.updateChart()
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('获取数据失败')
      } finally {
        this.loading = false
      }
    },
    async fetchLevels() {
      try {
        const res = await request.get('/api/dict/list', {
          params: {
            dict_key: 'game_level'
          }
        })
        if (res.data && Array.isArray(res.data)) {
          this.allLevels = res.data.map(item => ({
            value: item.dict_value,
            label: item.dict_value
          }))
          this.selectedLevels = this.allLevels.map(level => level.value)
        } else {
          console.error('获取比赛级别数据格式错误:', res)
          this.$message.error('获取比赛级别数据格式错误')
        }
      } catch (error) {
        console.error('获取比赛级别失败:', error)
        this.$message.error('获取比赛级别失败')
        this.allLevels = []
        this.selectedLevels = []
      }
    },
    calculateMonthlyAverages() {
      const monthlyData = new Map()
      
      this.chartData.months.forEach((month, index) => {
        let totalScore = 0
        let totalRank = 0
        let scoreCount = 0
        let rankCount = 0
        
        // 计算选中级别的总和
        this.selectedLevels.forEach(level => {
          const score = this.chartData.scores[level]?.[index]
          const rank = this.chartData.ranks[level]?.[index]
          const count = this.chartData.series.find(s => s.name === level)?.data[index] || 0
          
          // 只有当该级别在这个月有比赛时才计入平均值
          if (count > 0) {
            if (score !== null && score !== undefined) {
              totalScore += score * count  // 根据比赛数量加权
              scoreCount += count
            }
            if (rank !== null && rank !== undefined) {
              totalRank += rank * count    // 根据比赛数量加权
              rankCount += count
            }
          }
        })
        
        // 计算加权平均值
        monthlyData.set(month, {
          avgScore: scoreCount > 0 ? round(totalScore / scoreCount, 2) : null,
          avgRank: rankCount > 0 ? round(totalRank / rankCount, 2) : null
        })
      })

      return Array.from(monthlyData.entries()).map(([month, data]) => ({
        month,
        avgScore: data.avgScore,
        avgRank: data.avgRank
      })).sort((a, b) => a.month.localeCompare(b.month))
    },
    updateChart() {
      const monthlyAverages = this.calculateMonthlyAverages()
      
      // 重新构建柱状图数据
      const barData = this.chartData.months.map((_, index) => {
        const monthData = {}
        this.selectedLevels.forEach(level => {
          const series = this.chartData.series.find(s => s.name === level)
          if (series) {
            monthData[level] = series.data[index] || 0
          }
        })
        return monthData
      })

      // 颜色配置
      const colors = [
        '#3498DB', '#E74C3C', '#2ECC71', '#9B59B6', '#F1C40F',
        '#1ABC9C', '#E67E22', '#34495E', '#C0392B', '#27AE60'
      ]
      
      // 构建级别颜色映射
      const levelColorMap = {}
      this.selectedLevels.forEach((level, index) => {
        levelColorMap[level] = colors[index % colors.length]
      })
      
      // 构建新的系列数据
      const filteredSeries = this.selectedLevels.map((level, index) => ({
        name: level,
        type: 'bar',
        stack: 'total',
        data: this.chartData.months.map((_, idx) => {
          const series = this.chartData.series.find(s => s.name === level)
          return series ? series.data[idx] || 0 : 0
        }),
        itemStyle: {
          color: levelColorMap[level],
          borderRadius: [2, 2, 0, 0]
        }
      }))

      const option = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          confine: true,
          enterable: false,
          hideDelay: 100,
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 11
          },
          formatter: function(params) {
            if (!params || !Array.isArray(params) || params.length === 0) return ''
            const title = params[0].axisValue || ''
            
            // 颜色配置 - 与系列数据中的颜色保持一致
            const colors = [
              '#3498DB', '#E74C3C', '#2ECC71', '#9B59B6', '#F1C40F',
              '#1ABC9C', '#E67E22', '#34495E', '#C0392B', '#27AE60'
            ]
            
            // 构建级别颜色映射
            const levelColorMap = {}
            this.selectedLevels.forEach((level, index) => {
              levelColorMap[level] = colors[index % colors.length]
            })
            
            // 获取级别标签样式
            const getLevelTagStyle = (level) => {
              const color = levelColorMap[level]
              if (!color) return null
              // 将颜色转换为 rgba 格式用于背景
              const hexToRgba = (hex, alpha) => {
                const r = parseInt(hex.slice(1, 3), 16)
                const g = parseInt(hex.slice(3, 5), 16)
                const b = parseInt(hex.slice(5, 7), 16)
                return `rgba(${r}, ${g}, ${b}, ${alpha})`
              }
              const bg = hexToRgba(color, 0.2)
              return `display: inline-block; padding: 0px 4px; margin-right: 4px; border-radius: 2px; font-size: 10px; background-color: ${bg}; border: 1px solid ${color}; color: ${color}; line-height: 1.2;`
            }
            
            let totalCount = 0
            let content = ''
            
            params.forEach(param => {
              if (param.seriesType === 'bar') {
                const seriesName = param.seriesName
                const value = param.value
                totalCount += value
                
                // 检查是否是级别（在 selectedLevels 中）
                const isLevel = this.selectedLevels.includes(seriesName)
                if (isLevel && value > 0) {
                  const tagStyle = getLevelTagStyle(seriesName)
                  if (tagStyle) {
                    content += `<div><span style="${tagStyle}">${seriesName}</span>：${value}场</div>`
                  } else {
                    content += `<div>${seriesName}：${value}场</div>`
                  }
                } else if (value > 0) {
                  content += `<div>${seriesName}：${value}场</div>`
                }
              }
            })
            
            content += `<div>总计：${totalCount}场</div>`
            
            // 添加平均分和平均排名信息
            const monthData = monthlyAverages.find(item => item.month === params[0].axisValue)
            if (monthData) {
              if (monthData.avgScore !== null) {
                content += `<div>平均得分率：${monthData.avgScore.toFixed(2)}%</div>`
              }
              if (monthData.avgRank !== null) {
                content += `<div>平均排名：Top ${monthData.avgRank.toFixed(2)}%</div>`
              }
            }
            
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${title}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  ${content}
                </div>
              </div>
            `
          }.bind(this)
        },
        grid: {
          top: '10%',
          left: '5%',
          right: '5%',
          bottom: '10%',
          containLabel: true
        },
        legend: {
          data: this.selectedLevels,
          bottom: '0%',
          left: 'center',
          itemGap: 8,
          itemWidth: 10,
          itemHeight: 6,
          textStyle: {
            fontSize: 10,
            color: '#666'
          }
        },
        xAxis: {
          type: 'category',
          data: this.chartData.months,
          axisLabel: {
            fontSize: 10,
            rotate: 45,
            interval: 0
          },
          axisTick: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          name: '比赛数量',
          nameLocation: 'end',
          nameGap: 10,
          nameTextStyle: {
            fontSize: 10,
            color: '#666'
          },
          axisLabel: {
            fontSize: 10
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#E4E7ED'
            }
          },
          axisLine: {
            show: false
          }
        },
        series: filteredSeries,
        color: colors
      }

      this.chart.setOption(option, true)
    },
    handleExport() {
      const dpr = window.devicePixelRatio || 1
      const pixelRatio = Math.min(3, dpr * 1.5)
      const dataURL = this.chart.getDataURL({
        type: 'png',
        pixelRatio,
        backgroundColor: '#fff'
      })
      const blob = this.dataURLToBlob(dataURL)
      this.downloadBlob(blob, 'game_level_trend.png')
    },
    handleDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true

      this.$nextTick(() => {
        try {
          const chartDom = this.$refs.fullscreenChart
          if (!chartDom) return

          this.fullscreenChart = echarts.init(chartDom)
          const option = this.chart.getOption()
          
          // 全屏时调整配置
          option.grid = {
            left: '5%',
            right: '5%',
            bottom: '10%',
            top: '10%',
            containLabel: true
          }

          this.fullscreenChart.setOption(option)
          
          this.resizeHandler = _.debounce(() => {
            if (this.fullscreenChart) {
              this.fullscreenChart.resize()
            }
          }, 100)

          window.addEventListener('resize', this.resizeHandler)
        } catch (error) {
          console.error('初始化全屏图表失败:', error)
          this.$message.error('初始化图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handleDialogClosed() {
      this.isInitializing = false
    },
    handleFullscreen() {
      this.dialogVisible = true
    },
    closeDialog() {
      this.dialogVisible = false
    },
    handleExportFullscreen() {
      if (!this.fullscreenChart) return
      
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.fullscreenChart.getDataURL({
          type: 'png',
          pixelRatio,
          backgroundColor: '#fff'
        })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, 'game_level_trend.png')
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    handleLevelChange() {
      this.updateChart()
    },
    handleSelectAll() {
      this.selectedLevels = this.allLevels.map(level => level.value)
      this.updateChart()
    },
    handleReset() {
      this.selectedLevels = []
      this.updateChart()
    }
  }
}
</script>

<style scoped>
.game-count-trend-mobile {
  width: 100%;
  padding: 0;
  box-sizing: border-box;
  margin-bottom: 16px;
}

.header-select {
  width: 180px;
  margin-right: 8px;
}

.header-select :deep(.el-input__inner) {
  height: 32px;
  line-height: 32px;
  border-radius: 4px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 12px;
}

.header-select :deep(.el-input__inner)::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.header-select :deep(.el-input__suffix) {
  color: #ffffff;
}

.header-select :deep(.el-select__tags) {
  background: transparent;
  border: none;
  color: #ffffff;
}

.header-select :deep(.el-tag) {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #ffffff;
  font-size: 10px;
  height: 20px;
  line-height: 18px;
}



.chart-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
}

.card-title {
  font-size: 16px;
  font-weight: 500;
  color: white;
}

.card-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.card-actions .el-button {
  color: #ffffff;
  font-size: 16px;
  padding: 4px;
  margin-left: 8px;
}

.card-actions .el-button:hover {
  color: #409EFF;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.chart-container {
  width: 100%;
  height: 350px;
  padding: 0;
  box-sizing: border-box;
}

.select-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  border-bottom: 1px solid #EBEEF5;
  width: 100%;
}

.select-actions .el-button {
  font-size: 12px;
  padding: 4px 8px;
  height: 28px;
  line-height: 20px;
  flex: 1;
  text-align: center;
}

.select-actions .el-divider--vertical {
  height: 12px;
  margin: 0 2px;
}

.header-select :deep(.el-select-dropdown__wrap) {
  max-height: 200px;
}

.header-select :deep(.el-select-dropdown__item) {
  height: 36px;
  line-height: 36px;
  font-size: 14px;
}

.chart-dialog :deep(.el-dialog__header) {
  padding: 0;
  margin: 0;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
}

.dialog-actions {
  display: flex;
  gap: 8px;
}

.dialog-actions .el-button {
  color: #fff;
  padding: 4px;
}

.dialog-actions .el-button:hover {
  color: #BD6C73;
}

.fullscreen-chart {
  width: 100%;
  height: 500px;
  padding: 0;
  box-sizing: border-box;
}

/* 移动端优化 */
@media (max-width: 480px) {
  .game-count-trend-mobile {
    padding: 0;
    margin-bottom: 12px;
  }
  
  .header-select {
    width: 160px;
    margin-right: 6px;
  }
  
  .header-select :deep(.el-input__inner) {
    height: 28px;
    line-height: 28px;
    font-size: 11px;
  }
  
  .chart-container {
    height: 300px;
    padding: 0;
  }
  
  .card-header {
    padding: 12px 16px;
  }
  
  .card-title {
    font-size: 14px;
  }
  
  .card-actions .el-button {
    font-size: 14px;
    padding: 2px;
    margin-left: 4px;
  }
  
  .fullscreen-chart {
    height: 400px;
    padding: 0;
  }
}
</style>
